/*

  i o s  a r  l a u n c h
  iOSARLaunch

  :description:
  A class to assist with launching into AR on iOS devices.

*/

//
//  :code:
import { MESSAGE_GROUP_IOS, PLATFORM_IOS, SERVICE_AR_ASSISTANT, MODEL_CDN_BASE_URLS } from "../statics";
import EyekandyARLaunch from "../_abstract/EyekandyARLaunch";

export default class iOSARLaunch extends EyekandyARLaunch {

  constructor(options) {
    super(options)
    this.platform = PLATFORM_IOS
    this.group = MESSAGE_GROUP_IOS
    this.DEFAULT_BANNER_CONFIG = { text: {}, page: {}, button: { text: null } }
  }


  //
  //  :api:
  //  Public API to be used by external callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  renderLaunchARURL(service, arid, environment, qlOptions) {
    //
    //  Validate the inputs are valid before continuing.
    this._validateEnvironmentAndARIDAndService(environment, arid, service)
    //
    //  Return the iOS QuickLook URL.
    return this._renderIOSQuickLookURL(service, arid, environment, qlOptions)
  }

  renderModelURL(arid, environment) {
    //
    //  Validate the inputs.
    if (!environment) { environment = this.environment }
    this._validateEnvironmentAndARID(environment, arid)
    //
    //  Ok, render the URL for this model and return it.
    return `${MODEL_CDN_BASE_URLS[environment]}/USDZ/${arid}.usdz`
  }

  renderBannerConfig(options) {
    //
    //  :step 1:
    //  Correct any missing inputs.
    if (!options) { options = {} }

    //
    //  :step 2:
    //  Define the options we support.
    const OPTIONS = {
      textHeading: true,
      textContent: true,
      textButton: true,
      colourText: true,
      colourBackground: true,
      colourButtonText: true,
      colourButtonBackground: true,
    }
    const VALID_OPTIONS = Array.from(Object.keys(OPTIONS))
    //
    //  :step 2a:
    //  Validate the input options from the caller.
    //  TODO

    //
    //  :step 3:
    //  Write out some helper functions to assist with setting values clearly.
    const _get = (key) => { return options[key] || null }
    const _renderTextConfigBlock = () => {
      return { heading: _get("textHeading"), content: _get("textContent"), button: _get("textButton") }
    }
    const _renderColoursConfigBlock = () => {
      return {
        text: _get("colourText"), background: _get("colourBackground"), button: {
          text: _get("colourButtonText"), background: _get("colourButtonBackground")
        }
      }
    }

    //
    //  :step 4:
    //  Ok, now build up our "config", this must stay compatible with the way we render banners when
    //  launching into AR on iOS.
    const config = { colours: _renderColoursConfigBlock(), text: _renderTextConfigBlock() }

    //
    //  :step 5:
    //  Ok, now we really should apply some form of defaults, abstract to simple a "scheme" of colours.
    const COLOURS = { light: "#ffffff", dark: "#333333", button: { light: "#ffffff", dark: "#222222" } }
    if (!config.colours.text) { config.colours.text = COLOURS.light }
    if (!config.colours.button.text) { config.colours.button.text = COLOURS.button.light }

    if (!config.colours.background) { config.colours.background = COLOURS.dark }
    if (!config.colours.button.background) { config.colours.button.background = COLOURS.button.dark }
    //
    //  @Louis: Adding for debug.
    /*
    if (!config.text.heading) { config.text.heading = "heading" }
    if (!config.text.content) { config.text.heading = "content" }
    if (!config.text.button) { config.text.button = "button" }
    */

    //
    //  All done, return the config to the caller.
    return config
  }

  renderBannerConfigAsBase64(options) { return this._encode(JSON.stringify(this.renderBannerConfig(options))) }

  //
  //  :internal:
  //  Functions internal to the class, enums etc.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _getARLaunchOptions(service) {
    const options = {
      allowsContentScaling: 0,
      banner: { id: null, size: "small", config: null, options: null },
      callback: null, // The function to call when a user taps on the banner from within AR.
      context: null, // The JS context to bind the callback against.
    }
    if (service === SERVICE_AR_ASSISTANT) {
      options.allowsContentScaling = 1
    }
    return options
  }

  _getBannerURL(id, size) {
    if (!id) { id = "default" }
    if (!size) { size = "small" }
    return `https://cdn.pointandplace.com/services/ar-launch/banners/${size}/${id}/${id}.html`
  }

  _renderIOSQuickLookURL(service, arid, environment, qlOptions) {
    if (!qlOptions) { qlOptions = this._getARLaunchOptions(service) }
    //
    //  :step 1:
    //  Handle setting any dynamic values to our given options.
    if (qlOptions.banner) {
      if (qlOptions.banner.options) {
        //
        //  A user will give us their banner options as a flat object and we'll assemble it into a banner config.
        //  Note, this is ready to be used with our URL.
        qlOptions.banner.config = this.renderBannerConfigAsBase64(qlOptions.banner.options)
      }
    }

    //
    //  :step 2:
    //  Render our Model URL and see if we need to render a banner URL into the AR URL.
    const modelURL = this.renderModelURL(arid, environment)
    const hasBanner = qlOptions.banner.id && qlOptions.banner.size
    const allowsContentScalingIsSet = typeof qlOptions.allowsContentScaling !== "undefined"

    //
    //  :step 3:
    //  We can start to build up our intent, start with the base .usdz URL.
    let intent = modelURL
    //
    //  If we have any additional options, add a # to our intent.
    if (allowsContentScalingIsSet || hasBanner) { intent += "#" }
    //
    //  If we have QuickLook options, apply them.
    if (allowsContentScalingIsSet) { intent += `allowsContentScaling=${qlOptions.allowsContentScaling}&` }
    //
    //  If we have banner options, apply them.
    if (hasBanner) {
      //
      //  Ok, we need to assemble our banner URL from the CDN.
      let bannerURL = this._getBannerURL(qlOptions.banner.id)
      //
      //  Now we need to add on our parameterisation via a single get argument.
      bannerURL += `?config=${qlOptions.banner.config || "e30"}`
      //
      //  Now add this to our intent.
      intent += `custom=${encodeURIComponent(bannerURL)}&height=${qlOptions.banner.size}&`
    }
    //
    //  Trim the final character if it's an ampersand.
    intent = intent.replace(/&$/, '');
    //
    //  Return the intent to the caller.
    return intent
  }

}
